import queryString from 'query-string'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { ExtendLocales } from 'src/enums'
import { normalizeExtendLocale } from 'src/localization/unauthenticatedLocale'
import { computeBackgroundLocation } from 'src/navigation/backgroundLocationCalculation'
import { onboardingUrl } from 'src/navigation/urls'
import type { RootState } from 'src/redux/createRootReducer'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectDefaultLocaleFromState } from 'src/redux/selectors/session.selectors'

// Locale selector reimplemented here so the localization primitives don't import
// auth.selectors/router.selectors (they form a load-order cycle that crashes on
// import). Memoized so consumers get a stable locale string and don't re-render
// on navigation. Mirror selectNullSafeLocaleFromState; keep in sync until #7226.
const selectNullSafeLocale = createSelector(
  [
    selectUserListModelsFromState,
    (state: RootState) => state.auth.userId,
    (state: RootState) => state.auth.cspUserId,
    selectDefaultLocaleFromState,
    (state: RootState) => state.router.location,
    (state: RootState) => state.auth.isAuthenticated,
    (state: RootState) => state.auth.unauthedLocale,
  ],
  (
    users,
    userId,
    cspUserId,
    defaultLocale,
    location,
    isAuthenticated,
    unauthedLocale,
  ): ExtendLocales => {
    const user = userId !== undefined ? users[userId] : undefined
    const cspUser = cspUserId !== undefined ? users[cspUserId] : undefined
    const isOnboarding =
      !isAuthenticated ||
      computeBackgroundLocation(location).pathname === onboardingUrl()

    if (isOnboarding) {
      const { locale } = queryString.parse(location.search)
      const queryLocale = Array.isArray(locale) ? locale[0] : locale
      const normalizedQueryLocale = normalizeExtendLocale(queryLocale)
      if (normalizedQueryLocale !== undefined) {
        return normalizedQueryLocale
      }
      if (unauthedLocale !== undefined) return unauthedLocale
      return window.navigator.language as ExtendLocales
    }

    return user?.locale || cspUser?.locale || defaultLocale
  },
)

export const useNullSafeLocale = (): ExtendLocales =>
  useSelector(selectNullSafeLocale)
